import { useLayoutEffect, useState, useSyncExternalStore } from 'react';

// Filter hooks run with their page, but their options only show in the filter sidebar, which
// starts closed. Fetching every option list on page load fired ~25 requests per list view and
// queued the list's own data behind them, so option queries wait until something shows options.
const openOptionSurfaces = new Set<symbol>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => openOptionSurfaces.size > 0;

const notify = () => {
  listeners.forEach((listener) => listener());
};

/** `enabled` for a filter's option query: true while a filter sidebar or other options surface is mounted. */
export const useFilterOptionsEnabled = () => {
  const isOpen = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  // A page renders before the page it replaces unmounts, so its first render can still see the old page's
  // open sidebar. Enabling only after this hook's own first commit lets that sidebar's cleanup land first.
  const [hasCommitted, setHasCommitted] = useState(false);
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the post-commit render is the point: it reads the store after the old page's cleanup
    setHasCommitted(true);
  }, []);
  return hasCommitted && isOpen;
};

/** Mark the calling component as showing filter options (sidebar, drawer, or a picker built from a filter's `options`). */
export const useRequestFilterOptions = (active = true) => {
  // Before paint, so an opening sidebar renders option skeletons rather than a frame of empty lists.
  useLayoutEffect(() => {
    if (!active) {
      return;
    }
    const surface = Symbol('filter-options-surface');
    openOptionSurfaces.add(surface);
    notify();
    return () => {
      openOptionSurfaces.delete(surface);
      notify();
    };
  }, [active]);
};
